import { History, Location, LocationDescriptor } from "history";
import isNumber from "lodash-es/isNumber";
import { parse, stringify } from "query-string";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { match as Match } from "react-router";

import { CategoryKey } from "@/mk2/apps/bazaar/components/CategoryFacetOption";
import { OptionKey } from "@/mk2/apps/bazaar/components/FacetDropdownOption";
import { FacetsData } from "@/mk2/apps/bazaar/components/FacetsFilter";
import { PriceType } from "@/mk2/apps/bazaar/components/PriceFacetDropdown";
import { Checked } from "@/mk2/apps/bazaar/containers/BazaarGrid/BazaarGrid.actions";
import { NavigationHistoryState } from "@/mk2/apps/bazaar/helpers";
import { hashFilter } from "@/mk2/apps/bazaar/helpers/filter";
import { ProductEntity, ProductQuality } from "@/mk2/apps/bazaar/schemas";
import { ScrollToTop } from "@/mk2/components/ScrollToTop";
import { ComplaintEntity } from "@/mk2/containers/Complaint/Complaint.schemas";
import { getSessionStorage } from "@/mk2/helpers/storages";
import { url } from "@/mk2/helpers/urls";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import { Quality } from "@/mk2/reducers/request";
import { PAGE_SLUG } from "@/mk/autogenerated/translation";
import { dict } from "@/mk/bazaar/product/categories";
import {
    Filter,
    clearCategory,
    clearFacet,
    clearLocations,
    clearPlaces,
    resetPage,
    serializeFilterForPathname,
    serializeFilterForQuery,
    setCategory,
    setFacet,
    setLocations,
    setPlaces,
    setQuery,
    setSellers,
    setSettings,
} from "@/mk/bazaar/product/filter/filterUtils";

export const DEBOUNCE_TIME = 300;

export interface Props {
    filterFromRoute: Filter;
    hits: number;
    facets: FacetsData;
    products: ProductEntity[];
    loadingState: LoadingState;
    isMobile: boolean;

    location: Location<NavigationHistoryState>;
    history: History<NavigationHistoryState>;
    match: Match<any>;

    // Optional cached values
    complaintsPerProduct?: {
        [productId: number]: ComplaintEntity[];
    };
    checked?: Checked;
    selected?: number[];
}

export interface GridProps {
    filter: Filter;
    facets: FacetsData;
    hits: number;
    products: ProductEntity[];
    openedFacet: string;
    selectedParentCategory: string;
    loadingState: LoadingState;

    complaintsPerProduct: {
        [productId: number]: ComplaintEntity[];
    };
    checked: Checked;
    selected: number[];

    onSearchChange(search: string);
    onFacetClear(facetKey: string);
    onFacetChange(facetKey: string, optionKey: OptionKey, checked: boolean);
    onCategoryClear();
    onCategoryChange(category: CategoryKey);
    onPriceClear();
    onPriceChange(priceType: PriceType, value: number);
    onGridSortChange(optionKey: string);
    onSellersChange(optionKey: string);
    onQualityChange(quality: ProductQuality, checked: boolean);
    onPlacesRadiusChange(enabled: boolean);
    onValidChange(enabled: boolean);

    // My bazaar
    onMyGridSortChange(optionKey: string);
    onMyGridFilterChange(optionKey: string);

    // Wishlist
    onWishlistSortChange(optionKey: string);

    generateCategoryTo(category: CategoryKey): LocationDescriptor;
    pagerTo(page: number): LocationDescriptor;

    // Moderators and legacy
    onCleanSortChange(event: React.FormEvent<HTMLInputElement>);

    // Moderators
    onComplaintsChange(event: React.FormEvent<HTMLInputElement>);
    onNotMarkedChange(event: React.FormEvent<HTMLInputElement>);

    onFacetToggle(facetKey: string, opened: boolean);

    onSelectAll(event: React.MouseEvent<HTMLButtonElement>);
    onDeselectAll(event: React.MouseEvent<HTMLButtonElement>);
    onSelectProduct(productId: number, selected: boolean);
}

enum NeedChangeUrl {
    NO,
    IMMEDIATELY,
    DEBOUNCED,
}

export function canOpenFacet(openedFacet: string, facets: FacetsData): boolean {
    return openedFacet && (openedFacet === "price" || facets?.[openedFacet]?.length > 0);
}

const emptyArray = [];

function useGrid({
    filterFromRoute,
    facets,
    hits,
    products,
    loadingState,
    isMobile,
    location,
    history,
    match,
    complaintsPerProduct,
    checked,
    selected,
}: Props): GridProps {
    const needChangeUrl = useRef<NeedChangeUrl>(NeedChangeUrl.NO);
    const [filter, setFilter] = useState<Filter>(filterFromRoute);
    const [storedFilterFromRoute, setStoredFilterFromRoute] = useState<Filter>(filterFromRoute);
    const [storedFacets, setStoredFacets] = useState<FacetsData>(facets);
    const [storedHits, setStoredHits] = useState<number>(hits);
    const [storedProducts, setStoredProducts] = useState<ProductEntity[]>(products);
    const [storedComplaintsPerProduct, setStoredComplaintsPerProduct] = useState<{
        [productId: number]: ComplaintEntity[];
    }>(complaintsPerProduct);
    const [storedChecked, setStoredChecked] = useState<Checked>(checked);
    const [storedSelected, setStoredSelected] = useState<number[]>(selected || emptyArray);
    const [storedLoadingState, setStoredLoadingState] = useState<LoadingState>(loadingState);

    // If filter from route has changed - reset stored filter
    if (hashFilter(filterFromRoute) !== hashFilter(storedFilterFromRoute)) {
        setFilter(filterFromRoute);
        setStoredFilterFromRoute(filterFromRoute);
    }

    // Keep products, facets and hits in state during loading so that we can keep them rendered
    if (products !== storedProducts && isSuccess(loadingState)) {
        setStoredProducts(products);
        setStoredComplaintsPerProduct(complaintsPerProduct);
        setStoredChecked(checked);
        setStoredSelected(emptyArray);
    }
    if (facets && facets !== storedFacets) {
        setStoredFacets(facets);
    }
    if (isNumber(hits) && hits !== storedHits) {
        setStoredHits(hits);
    }
    // Skip INIT loading state when updating component
    // because LoadingSwitch is causing unnecessary re-mount
    // when switching to new filter
    if (loadingState !== LoadingState.INIT && loadingState !== storedLoadingState) {
        setStoredLoadingState(loadingState);
    }

    const qs = parse(location.search) || {};
    const openedFacet = qs.opened as string;
    const selectedParentCategory = (qs["parent-category"] as string) || null;

    // Handlers
    const onSearchChange = useCallback(
        (search: string) => {
            setFilter((s) =>
                resetPage(setSettings(setQuery(s, search), { gridSort: "score_new" })),
            );
            needChangeUrl.current = NeedChangeUrl.IMMEDIATELY;
        },
        [setFilter],
    );

    const onFacetClear = useCallback(
        (facetKey: string) => {
            setFilter((s) =>
                resetPage(
                    facetKey === "locations"
                        ? clearLocations(s)
                        : facetKey === "places"
                          ? clearPlaces(s)
                          : clearFacet(s, facetKey),
                ),
            );
            needChangeUrl.current = NeedChangeUrl.IMMEDIATELY;
        },
        [setFilter],
    );

    const onFacetChange = useCallback(
        (facetKey: string, optionKey: OptionKey, check: boolean) => {
            setFilter((s) =>
                resetPage(
                    facetKey === "locations"
                        ? setLocations(s, optionKey as number, check)
                        : facetKey === "places"
                          ? setPlaces(s, optionKey as number, check)
                          : setFacet(s, facetKey, optionKey as string, check),
                ),
            );
            needChangeUrl.current = NeedChangeUrl.DEBOUNCED;
        },
        [setFilter],
    );

    const onCategoryClear = useCallback(() => {
        setFilter((s) => resetPage(clearCategory(s)));
        needChangeUrl.current = NeedChangeUrl.IMMEDIATELY;
    }, [setFilter]);

    const onCategoryChange = useCallback(
        (category: CategoryKey) => {
            setFilter((s) => resetPage(setCategory(s, category)));
            needChangeUrl.current = NeedChangeUrl.IMMEDIATELY;
        },
        [setFilter],
    );

    const onPriceClear = useCallback(() => {
        setFilter((s) =>
            resetPage({
                ...s,
                price_hi: null,
                price_lo: null,
            }),
        );
        needChangeUrl.current = NeedChangeUrl.IMMEDIATELY;
    }, [setFilter]);

    const onPriceChange = useCallback(
        (priceType: PriceType, value: number) => {
            if (value < 0) {
                value = 0;
            }

            if (value > Number.MAX_SAFE_INTEGER) {
                value = Number.MAX_SAFE_INTEGER;
            }
            setFilter((s) =>
                resetPage({
                    ...s,
                    price_lo: priceType === "min" ? value : s.price_lo,
                    price_hi: priceType === "max" ? value : s.price_hi,
                }),
            );
            needChangeUrl.current = NeedChangeUrl.DEBOUNCED;
        },
        [setFilter],
    );

    const onComplaintsChange = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            setFilter((s) =>
                resetPage({
                    ...s,
                    complaints: event.currentTarget.checked,
                }),
            );
            needChangeUrl.current = NeedChangeUrl.DEBOUNCED;
        },
        [setFilter],
    );

    const onNotMarkedChange = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            setFilter((s) =>
                resetPage({
                    ...s,
                    notMarked: event.currentTarget.checked,
                }),
            );
            needChangeUrl.current = NeedChangeUrl.DEBOUNCED;
        },
        [setFilter],
    );

    const onCleanSortChange = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            setFilter((s) =>
                resetPage(setSettings(s, { cleanSort: !event.currentTarget.checked })),
            );
            needChangeUrl.current = NeedChangeUrl.DEBOUNCED;
        },
        [setFilter],
    );

    const onGridSortChange = useCallback(
        (optionKey: Filter["settings"]["gridSort"]) => {
            setFilter((s) => resetPage(setSettings(s, { gridSort: optionKey })));
            needChangeUrl.current = NeedChangeUrl.IMMEDIATELY;
        },
        [setFilter],
    );

    const onMyGridFilterChange = useCallback(
        (optionKey: Filter["settings"]["myGridFilter"]) => {
            setFilter((s) => resetPage(setSettings(s, { myGridFilter: optionKey })));
            needChangeUrl.current = NeedChangeUrl.IMMEDIATELY;
        },
        [setFilter],
    );

    const onMyGridSortChange = useCallback(
        (optionKey: Filter["settings"]["myGridSort"]) => {
            setFilter((s) => resetPage(setSettings(s, { myGridSort: optionKey })));
            needChangeUrl.current = NeedChangeUrl.IMMEDIATELY;
        },
        [setFilter],
    );

    const onWishlistSortChange = useCallback(
        (optionKey: Filter["settings"]["wishlistSort"]) => {
            setFilter((s) => resetPage(setSettings(s, { wishlistSort: optionKey })));
            needChangeUrl.current = NeedChangeUrl.IMMEDIATELY;
        },
        [setFilter],
    );

    const onSellersChange = useCallback(
        (optionKey: string) => {
            setFilter((s) => resetPage(setSellers(s, optionKey)));
            needChangeUrl.current = NeedChangeUrl.IMMEDIATELY;
        },
        [setFilter],
    );

    // TODO consider to clean up ProductQuality.JOBS
    const allButJobs = (qualities: Quality[]) => qualities.filter((q) => q !== ProductQuality.JOBS);

    const onQualityChange = useCallback(
        (quality: ProductQuality, check: boolean) => {
            setFilter((s) =>
                resetPage(
                    setSettings(s, {
                        quality: check
                            ? [
                                  ...allButJobs(s.settings.quality).filter((q) => q !== quality),
                                  quality,
                              ] // prevent duplication
                            : allButJobs(s.settings.quality).length === 1
                              ? allButJobs(s.settings.quality)
                              : allButJobs(s.settings.quality).filter((q) => q !== quality),
                    }),
                ),
            );
            needChangeUrl.current = NeedChangeUrl.DEBOUNCED;
        },
        [setFilter],
    );

    const onPlacesRadiusChange = useCallback(
        (enabled: boolean) => {
            setFilter((s) =>
                resetPage(
                    setSettings(s, {
                        placesRadius: enabled,
                    }),
                ),
            );
            needChangeUrl.current = NeedChangeUrl.DEBOUNCED;
        },
        [setFilter],
    );

    const onValidChange = useCallback(
        (enabled: boolean) => {
            setFilter((s) =>
                resetPage({
                    ...s,
                    valid: enabled,
                }),
            );
            needChangeUrl.current = NeedChangeUrl.DEBOUNCED;
        },
        [setFilter],
    );

    const generateCategoryTo = useCallback(
        (category: CategoryKey): LocationDescriptor => {
            const cat = dict()[category];
            const { opened, "parent-category": parentCategory, ...restQs } = qs;

            return {
                ...location,
                pathname:
                    isMobile && cat.children && cat.children.length
                        ? location.pathname
                        : url(match.path, {
                              ...match.params,
                              category: cat && cat.slug,
                          }),
                search:
                    isMobile && cat.children && cat.children.length
                        ? stringify({
                              ...restQs,
                              opened: "cats",
                              "parent-category": cat.name,
                          })
                        : stringify({
                              ...restQs,
                          }),
                state: { dontScrollOnPush: !isMobile },
            };
        },
        [location, isMobile, match, qs],
    );

    const pagerTo = useCallback(
        (page: number): LocationDescriptor => {
            // Generate location description for new page
            const cat = filter.cats.length ? dict()[filter.cats[0]] : null;
            return {
                pathname: url(match.path, {
                    ...match.params,
                    category: cat && cat.slug,
                    filters: serializeFilterForPathname(filter),
                    page: page > 1 ? `${PAGE_SLUG}-${page}` : undefined,
                }),
                search: stringify(serializeFilterForQuery(filter)),
                state: {
                    // chceme aby sa stranka neodscrollovala a bud
                    // 1) ostala na tom istom mieste
                    // 2) alebo po nacitani sami dalsim kodom odscrollujeme na prvy prispevok
                    dontScrollOnPush: true,
                    // ked chodim medzi strankami cez prev/next a potom dam 'back',
                    // tak sa chcem vratit na stranku odkial som prisiel a nie na
                    // tuto istu stranku len s inym ?page=...
                    skipOnBack: true,
                },
            };
        },
        [filter, match],
    );

    const onFacetToggle = useCallback(
        (facetKey: string, isOpened: boolean) => {
            if (isMobile) {
                if (openedFacet === facetKey && !isOpened) {
                    history.goBack();
                } else if (canOpenFacet(facetKey, storedFacets)) {
                    // Store scroll position in session storage when
                    // facet window is opened
                    // NOTE: Alternative is to keep scroll position in the URL
                    //  but then sharing URL will contain that query value
                    getSessionStorage().setItem("useGrid__scroll", `${ScrollToTop.getScrollTop()}`);
                    history.push({
                        ...location,
                        state: {}, // Clear state
                        search: stringify({
                            ...qs,
                            opened: facetKey,
                        }),
                    });
                }
            }
        },
        [history, isMobile, location, qs, openedFacet, storedFacets],
    );

    const onSelectAll = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setStoredSelected(products.map((p) => p.id));
        },
        [setStoredSelected, products],
    );

    const onDeselectAll = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setStoredSelected(emptyArray);
        },
        [setStoredSelected],
    );

    const onSelectProduct = useCallback(
        (productId: number, sel: boolean) => {
            setStoredSelected((s) => (sel ? [...s, productId] : s.filter((i) => i !== productId)));
        },
        [setStoredSelected],
    );

    // Debounce filter changes and reflect in URL
    const changeUrl = () => {
        const cat = filter.cats.length ? dict()[filter.cats[0]] : null;
        const newPathname = url(match.path, {
            ...match.params,
            category: cat && cat.slug,
            filters: serializeFilterForPathname(filter),
            page: filter.page > 1 ? `${PAGE_SLUG}-${filter.page}` : undefined,
        });
        const newSearch =
            "?" +
            stringify(
                {
                    ...serializeFilterForQuery(filter),
                    opened: openedFacet,
                },
                { skipEmptyString: true },
            );
        if (location.pathname !== newPathname || location.search !== newSearch) {
            if (openedFacet && openedFacet !== "cats") {
                // Facets are always multiselects - so to keep
                // back button work as intended we replace current URL
                history.replace({
                    pathname: newPathname,
                    search: newSearch,
                    state: { dontScrollOnPush: true },
                });
            } else {
                history.push({
                    pathname: newPathname,
                    search: newSearch,
                    state: { dontScrollOnPush: true },
                });
            }
        }
        needChangeUrl.current = NeedChangeUrl.NO;
    };

    useEffect(() => {
        if (needChangeUrl.current === NeedChangeUrl.DEBOUNCED) {
            const timer = window.setTimeout(changeUrl, DEBOUNCE_TIME);
            return () => {
                window.clearTimeout(timer);
            };
        } else if (needChangeUrl.current === NeedChangeUrl.IMMEDIATELY) {
            changeUrl();
        }
    }, [filter, needChangeUrl]);

    return {
        filter,
        facets: storedFacets,
        hits: storedHits,
        products: storedProducts,
        openedFacet,
        selectedParentCategory,
        loadingState: storedLoadingState,

        complaintsPerProduct: storedComplaintsPerProduct,
        checked: storedChecked,
        selected: storedSelected,

        generateCategoryTo,
        pagerTo,

        onSearchChange,
        onFacetClear,
        onFacetChange,
        onCategoryClear,
        onCategoryChange,
        onPriceClear,
        onPriceChange,
        onGridSortChange,
        onSellersChange,
        onQualityChange,
        onPlacesRadiusChange,
        onValidChange,

        // My bazaar
        onMyGridFilterChange,
        onMyGridSortChange,

        // Wishlist
        onWishlistSortChange,

        // Moderators and legacy
        onCleanSortChange,

        // Moderators
        onComplaintsChange,
        onNotMarkedChange,

        onFacetToggle,

        onSelectAll,
        onDeselectAll,
        onSelectProduct,
    };
}

export default useGrid;
